import { lazy, Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/features/auth/AuthProvider";
import { OfflineProvider } from "@/features/offline/OfflineProvider";
import { Toaster } from "@/components/ui/sonner";
import { ThemeColorSync } from "@/components/ThemeToggle";
import { AppRoutes } from "@/routes/AppRoutes";
import { queryClient } from "@/lib/queryClient";

// Query devtools, lazy + DEV-gated so they're dropped from the production
// bundle entirely rather than shipped-but-hidden.
const Devtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* OfflineProvider owns the write-queue lifecycle (replay on reconnect)
          and exposes online/pending state to the offline indicator. It sits
          under QueryClientProvider so it can invalidate queries post-sync. */}
      <OfflineProvider>
        {/* Studio Park design system: Staff Hub is LIGHT by default (dense
            tables and forms read at length, often outdoors in glare). Dark
            is the canonical brand and one tap away via ThemeToggle; the
            choice persists. No system-following — the default is a
            deliberate per-app decision, not the OS's. */}
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
          <ThemeColorSync />
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </OfflineProvider>
      {Devtools && (
        <Suspense fallback={null}>
          <Devtools initialIsOpen={false} buttonPosition="bottom-left" />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
